import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NumbersIcon from "@mui/icons-material/Numbers";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PercentIcon from "@mui/icons-material/Percent";
import {
  Divider,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { isNil, sumBy } from "lodash";
import { Chart } from "primereact/chart";
import { Column, type ColumnBodyOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useMemo, useRef, useState } from "react";
import {
  type DocumentIngestionPipelineFilter,
  useOrderVolumeByUserReportQuery,
} from "../../../generated/graphql";
import TabPanel from "../../common/tab-panel/tab-panel";
import DateRangePicker, { type DateOption } from "../../date-range-picker";
import {
  getOrderSourceBucketByOrderSource,
  initialOrderVolumeData,
  OrderSource,
  StatisticsView,
} from "./utils";

type OrderVolumeUserData = {
  userUuid: string;
  name: string | null | undefined;
  userEmail: string;
  userTerminalName: string | null | undefined;
  [OrderSource.MANUAL]: number;
  [OrderSource.EMAIL_DOC_SCAN]: number;
  [OrderSource.UPLOADED_DOC_SCAN]: number;
  [OrderSource.API]: number;
  [OrderSource.EDI]: number;
  [OrderSource.CSV_IMPORT]: number;
  total: number;
};

const defaultChartRowsPerPage = 14;
const expandedChartRowsPerPage = 45;

const OrderVolumeByUserStatistics = ({
  tab,
  companyUuid,
  pipeline,
}: {
  readonly tab: StatisticsView;
  readonly companyUuid: string;
  readonly pipeline: DocumentIngestionPipelineFilter;
}) => {
  const [dateOption, setDateOption] = useState<DateOption>({
    startDate: dayjs(new Date()).subtract(7, "days").toDate(),
    endDate: null,
  });
  const tableRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showPercentage, setShowPercentage] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const { data: orderVolumeByUserData, loading } =
    useOrderVolumeByUserReportQuery({
      variables: {
        orderVolumeByUserInput: {
          companyUuid,
          startDate: dateOption.startDate,
          endDate: dateOption.endDate,
          pipeline,
        },
      },
      fetchPolicy: "cache-first",
    });

  const chartRowsPerPage = useMemo(() => {
    return isExpanded ? expandedChartRowsPerPage : defaultChartRowsPerPage;
  }, [isExpanded]);

  const orderVolumeData = useMemo(() => {
    return orderVolumeByUserData?.orderVolumeByUserReport.map((userData) => {
      const data: OrderVolumeUserData = {
        ...initialOrderVolumeData,
        ...userData,
        name: userData.userEmail,
        userTerminalName: userData.userTerminalName,
      };
      data.total = sumBy(
        Object.values(OrderSource),
        (source) =>
          getOrderSourceBucketByOrderSource(source, userData.sourceAmounts)
            ?.totalCount ?? 0,
      );
      for (const source of Object.values(OrderSource)) {
        data[source] =
          getOrderSourceBucketByOrderSource(source, userData.sourceAmounts)
            ?.totalCount ?? 0;
        if (showPercentage) {
          const roundedPercentage = Math.round(
            (data[source] / data.total) * 100,
          );
          data[source] = Number.isNaN(roundedPercentage)
            ? 0
            : roundedPercentage;
        }
      }
      return data;
    });
  }, [orderVolumeByUserData, showPercentage]);

  const chartData = useMemo(() => {
    if (isNil(orderVolumeData)) return {};
    const sortedVolumeData = orderVolumeData
      .sort((a, b) => b[OrderSource.MANUAL] - a[OrderSource.MANUAL])
      .slice(page * chartRowsPerPage, (page + 1) * chartRowsPerPage);
    return {
      labels: sortedVolumeData.map((d) => d.name ?? d.userEmail),
      datasets: Object.values(OrderSource).map((source) => ({
        label: source,
        data: sortedVolumeData.map((data) => data[source]),
        hidden: source !== OrderSource.MANUAL,
      })),
    };
  }, [orderVolumeData, page, chartRowsPerPage]);

  const sourceTemplate = (
    data: OrderVolumeUserData,
    options: ColumnBodyOptions,
  ) => {
    return (
      <span>
        {data[options.field as OrderSource] ?? ""}
        {showPercentage ? "%" : ""}
      </span>
    );
  };

  const header = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        border: 1,
        borderBottom: 0,
        borderColor: "#E9ECEF",
        backgroundColor: "#f8f9fa",
        p: 0.5,
      }}
    >
      <Stack>
        <Typography sx={{ fontWeight: "bold" }}>Order volume</Typography>
        <Typography variant="caption">(by user)</Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
      >
        {tab === StatisticsView.TABLE && (
          <IconButton
            size="small"
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              tableRef.current?.exportCSV();
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        )}
        {tab === StatisticsView.CHART && (
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={orderVolumeData?.length ?? 0}
            page={page}
            backIconButtonProps={{
              style: {
                padding: 4,
              },
            }}
            nextIconButtonProps={{
              style: {
                padding: 4,
              },
            }}
            rowsPerPage={chartRowsPerPage}
            onPageChange={(e, newPage) => {
              setPage(newPage);
            }}
          />
        )}
        <Divider flexItem orientation="vertical" />
        <DateRangePicker
          dateOption={dateOption}
          onChange={(newDateOption) => {
            setDateOption(newDateOption);
          }}
        />
        <Divider flexItem orientation="vertical" />
        <Typography sx={{ fontWeight: "bold" }}>Metric</Typography>
        <Tooltip title="Counts">
          <IconButton
            disabled={!showPercentage}
            size="small"
            onClick={() => {
              setShowPercentage(false);
            }}
          >
            <NumbersIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Percentage of total">
          <IconButton
            disabled={showPercentage}
            size="small"
            onClick={() => {
              setShowPercentage(true);
            }}
          >
            <PercentIcon />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <IconButton
          size="small"
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
        </IconButton>
      </Stack>
    </Stack>
  );

  return (
    <Stack sx={{ overflowY: "scroll" }}>
      <div
        style={
          isExpanded
            ? {
                position: "absolute",
                zIndex: 10_000,
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "white",
              }
            : {}
        }
      >
        {header}
        <TabPanel panelValue={StatisticsView.CHART} selectedValue={tab}>
          <Chart
            height={isExpanded ? "90vh" : "320px"}
            type="bar"
            data={chartData}
            options={{
              indexAxis: "y",
              maintainAspectRatio: false,
              aspectRatio: 0.8,
              scales: {
                x: {
                  ticks: {
                    font: {
                      weight: 500,
                    },
                    precision: 0,
                  },
                  grid: {
                    display: false,
                    drawBorder: false,
                  },
                },
                y: {
                  grid: {
                    drawBorder: false,
                  },
                },
              },
            }}
          />
        </TabPanel>
        <TabPanel panelValue={StatisticsView.TABLE} selectedValue={tab}>
          <DataTable
            ref={tableRef}
            key={`${companyUuid}-open-${isExpanded}`}
            removableSort
            showGridlines
            scrollable
            stripedRows
            value={orderVolumeData}
            sortMode="multiple"
            rows={10}
            loading={loading}
            dataKey="id"
            emptyMessage="No data"
            size="small"
            scrollHeight={isExpanded ? "100vh" : "325px"}
            virtualScrollerOptions={{ itemSize: 25 }}
          >
            <Column
              sortable
              filter
              field="name"
              header="User"
              filterPlaceholder="Search by user name"
            />
            <Column
              sortable
              filter
              field="userTerminalName"
              header="Terminal"
              filterPlaceholder="Search by terminal name"
            />
            {Object.values(OrderSource).map((source) => (
              <Column
                key={source}
                sortable
                header={source}
                field={source}
                body={sourceTemplate}
              />
            ))}
            <Column sortable header="Total" field="total" />
          </DataTable>
        </TabPanel>
      </div>
    </Stack>
  );
};
export default OrderVolumeByUserStatistics;
